import { memo, useMemo, useState } from "react";
import Home6LineIcon from "remixicon-react/Home6LineIcon";
import { Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { usePopin } from "$components/Popin/usePopin";
import { FormHeader } from "$components/Forms/FormHeader";
import EntrepotForm from "$containers/Entrepot/EntrepotDetails/EntrepotForm";
import useWarehouseForm from "$hooks/useWarehouseForm";
import { InfoContainer, Text } from "$components";
import JTable from "$components/JTable/JTable";
import { useStyles } from "../styles";
import { TitleInfoContainer } from "$components/InfoContainer/TitleInfoContainer";
import { SimpleMuiButton } from "$components/Buttons/MuiButton";
import { CompanyType } from "$types";
import { usePatchWarehouse } from "../hooks/usePatchWarehouse";
import { Loader } from "$components/Layout/Loader";

/** Convertit le format thead/bodyData legacy de usePatchWarehouse en colonnes/données JTable. */
function DisplayWareHouse({ addButton, data }: any) {
  const classes = useStyles();
  const [pageIndex, setPageIndex] = useState(0);

  const columns = useMemo(
    () =>
      (data.head ?? []).map((col: any) => ({
        id: col.id,
        Header: col.label ?? col.id,
        Cell: ({ row }: any) => row.original[col.id],
        ...(col.width ? { meta: { width: col.width } } : {}),
      })),
    [data.head]
  );

  const tableData = useMemo(
    () =>
      (data.body ?? []).map((row: any[]) => {
        const obj: Record<string, any> = {};
        (data.head ?? []).forEach((col: any, i: number) => {
          obj[col.id] = row[i]?.value ?? row[i] ?? null;
        });
        if (row[0]?.onClickAction) obj._onClick = row[0].onClickAction;
        return obj;
      }),
    [data.body, data.head]
  );

  return (
    <Box>
      <Box className={classes.warehouseTableTitle}>
        <Text>{data.title}</Text>
        {addButton}
      </Box>
      <JTable
        data={tableData}
        columns={columns}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        rowsPerPage={10}
        hideCheckboxes
        hideFilters
        usePaper={false}
        noDataText={data.bodyMissing}
        className={classes.noPaddingTable}
        rowProps={(row) =>
          row.original._onClick
            ? { onClick: row.original._onClick, style: { cursor: "pointer" } }
            : { className: "nopointer" }
        }
      />
    </Box>
  );
}

const getTableHead = (latestColumnName?: string) => {
  return [
    { id: "user", label: "Entrepôt", width: 80 },
    { id: "adresse", label: "Adresse", width: 80 },
    { id: "codePostal", label: "Code Postal", width: 80 },
    { id: "ville", label: "Ville", width: 80 },
    { id: "contact", label: "Contact(s)", width: 80 },
    {
      id: latestColumnName || "latest_column",
      label: latestColumnName || "",
      width: 80,
    },
  ];
};

export const ResumeWarehouse = memo(function ResumeWarehouse({
  company,
  companyId,
  selectedTab,
  contributors,
  isDisabled,
  redirect,
  clients,
}: any) {
  const { Popin, togglePopin, openPopin, PopinProps } = usePopin();
  const { handleSubmit, error, submitting } = useWarehouseForm({
    togglePopinExt: togglePopin,
  });
  const classes = useStyles();
  const patchWarehouse = usePatchWarehouse({
    companyId,
    redirect,
    selectedTab,
    contributors,
    clients,
  });

  // TODO may be move this to the usePatchWarehouse
  // Mémoïsé pour éviter de reconstruire les tableaux (dont getTableHead qui
  // retourne un nouveau tableau à chaque appel) à chaque rendu, ce qui
  // entraînait des mises à jour en cascade dans les JTable enfants.
  const data = useMemo(
    () => [
      {
        title: "Entrepôts propres",
        bodyMissing: `Aucun entrepôt propre n'est rattaché ${patchWarehouse.missing}`,
        head: getTableHead(
          CompanyType.CONTRIBUTOR === selectedTab && "Client(s) rattaché(s)"
        ),
        body: patchWarehouse.renderWareHouse,
        withButton: true,
      },
      ...(selectedTab.includes(CompanyType.CONTRIBUTOR)
        ? [
            {
              title: "Entrepôts clients",
              bodyMissing: `Aucun entrepôt client n'est rattaché à l'intervenant`,
              head: getTableHead("Client"),
              body: patchWarehouse.renderClientWarehouse,
            },
          ]
        : []),
      ...(selectedTab.includes(CompanyType.CLIENT)
        ? [
            {
              title: "Entrepôts intervenants",
              bodyMissing: `Aucun entrepôt intervenant n'est rattaché au client`,
              head: getTableHead("Intervenant"),
              body: patchWarehouse.renderContributorWareHouse,
            },
          ]
        : []),
      ...(selectedTab.includes(CompanyType.CLIENT)
        ? [
            {
              title: "Entrepôts Logisticiens partenaires Batail-Log",
              bodyMissing: `Aucun entrepôt logisticien n'est rattaché au client`,
              head: getTableHead("Logisticien"),
              body: patchWarehouse.renderLogisticianWareHouse,
            },
          ]
        : []),
    ],
    [
      selectedTab,
      patchWarehouse.missing,
      patchWarehouse.renderWareHouse,
      patchWarehouse.renderClientWarehouse,
      patchWarehouse.renderContributorWareHouse,
      patchWarehouse.renderLogisticianWareHouse,
    ]
  );

  if (patchWarehouse.isLoading) {
    return <Loader />;
  }

  return (
    <>
      <InfoContainer
        className={classes.fullWidthContainer}
        isToggleable
        isLoading={patchWarehouse.isLoading}
        leftHeader={
          <TitleInfoContainer title="Entrepôt(s)" icon={<Home6LineIcon />} />
        }
      >
        <Box className={classes.warehouseTables}>
          {data.map((d, i) => (
            <DisplayWareHouse
              key={`warehouse-${i}`}
              data={d}
              addButton={
                d.withButton ? (
                  <SimpleMuiButton
                    disabled={isDisabled}
                    className={`${classes.addWarehouseBtn} ${
                      company?.isActive === false ? classes.disabledCta : ""
                    }`}
                    onClick={company?.isActive ? openPopin : undefined}
                    icon={<AddIcon />}
                  >
                    Ajouter un entrepôt
                  </SimpleMuiButton>
                ) : null
              }
            />
          ))}
        </Box>
      </InfoContainer>

      <Popin {...PopinProps}>
        <FormHeader title="Créer un entrepôt" />
        <EntrepotForm
          parentCompanyId={company?._id}
          onSubmit={handleSubmit}
          onReset={togglePopin}
          isSubmitting={submitting}
          entrepotError={error}
        />
      </Popin>
    </>
  );
});
